import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  showErrorToast,
  showSuccessToast,
} from "src/utils/Toast/SnackbarProvider";
import apiInstance from "src/utils/apiAuth";
import * as constants from "../utils/constants";

const AssistantContext = createContext(null);

export const AssistantProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);

  const [chatSessions, setChatSessions] = useState([]);
  const [sessionsNextPage, setSessionsNextPage] = useState(null);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isFetchingMoreSessions, setIsFetchingMoreSessions] = useState(false);

  const [messagesCache, setMessagesCache] = useState({});

  // Track streaming state
  const streamingRef = useRef(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [loadingStage, setLoadingStage] = useState(null);
  const abortControllerRef = useRef(null);

  const fetchSessions = useCallback(
    async (url = null) => {
      const isLoadMore = !!url;
      if (isLoadMore && isFetchingMoreSessions) return;
      if (!isLoadMore && isLoadingSessions) return;

      if (isLoadMore) setIsFetchingMoreSessions(true);
      else setIsLoadingSessions(true);

      try {
        const endpoint = url || "user-chats";
        const response = await apiInstance.get(endpoint, {
          headers: {
            Authorization: `Token ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        });

        const newSessions = response.data.results || [];
        const next = response.data.next;

        const dedupeByChatId = (sessions) => {
          const byId = new Map();
          sessions.forEach((session) => {
            const existing = byId.get(session.chat_id);
            if (!existing) {
              byId.set(session.chat_id, session);
              return;
            }
            const existingDate = new Date(existing.created_at);
            const nextDate = new Date(session.created_at);
            if (nextDate > existingDate) {
              byId.set(session.chat_id, session);
            }
          });
          return Array.from(byId.values());
        };

        setSessionsNextPage(next);
        setChatSessions((prev) => {
          if (isLoadMore) {
            const existingIds = new Set(prev.map((s) => s.chat_id));
            const uniqueNew = newSessions.filter(
              (s) => !existingIds.has(s.chat_id),
            );
            return dedupeByChatId([...prev, ...uniqueNew]).sort(
              (a, b) => new Date(b.created_at) - new Date(a.created_at),
            );
          }
          return dedupeByChatId(newSessions).sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at),
          );
        });
      } catch (err) {
        console.error("Failed to fetch sessions", err);
        showErrorToast(err, "Failed to load sessions");
      } finally {
        if (isLoadMore) setIsFetchingMoreSessions(false);
        else setIsLoadingSessions(false);
      }
    },
    [isFetchingMoreSessions, isLoadingSessions],
  );

  const fetchMessages = useCallback(async (chatId, url = null) => {
    if (!chatId) return;
    const isLoadMore = !!url;

    let shouldStop = false;
    setMessagesCache((prev) => {
      const currentChatState = prev[chatId] || {
        isLoading: false,
        isFetchingMore: false,
      };
      if (isLoadMore && currentChatState.isFetchingMore) {
        shouldStop = true;
        return prev;
      }
      if (!isLoadMore && currentChatState.isLoading) {
        shouldStop = true;
        return prev;
      }
      return {
        ...prev,
        [chatId]: {
          ...(prev[chatId] || { messages: [], next: null }),
          isLoading: !isLoadMore,
          isFetchingMore: isLoadMore,
        },
      };
    });

    if (shouldStop) return;

    try {
      const endpoint = url || `user-chats/${chatId}/`;
      const response = await apiInstance.get(endpoint, {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
      });

      const newMessages = response.data.results || [];
      const next = response.data.next;

      setMessagesCache((prev) => {
        const currentData = prev[chatId] || { messages: [], next: null };
        let updatedMessages;
        if (isLoadMore) {
          const existingIds = new Set(currentData.messages.map((m) => m.id));
          const uniqueNew = newMessages.filter((m) => !existingIds.has(m.id));
          updatedMessages = [...currentData.messages, ...uniqueNew];
        } else {
          updatedMessages = newMessages;
        }

        return {
          ...prev,
          [chatId]: {
            messages: updatedMessages.sort(
              (a, b) => new Date(a.created_at) - new Date(b.created_at),
            ),
            next: next,
            isLoading: false,
            isFetchingMore: false,
          },
        };
      });
    } catch (err) {
      console.error(`Failed to fetch messages for chat ${chatId}`, err);
      showErrorToast(err, "Failed to load messages");
      setMessagesCache((prev) => ({
        ...prev,
        [chatId]: {
          ...(prev[chatId] || {}),
          isLoading: false,
          isFetchingMore: false,
        },
      }));
    }
  }, []);

  const sendMessage = async (
    message,
    mode = "not_outreached",
    webSearch = false,
  ) => {
    if (streamingRef.current) {
      return;
    }

    let activeChatId = currentChatId;
    const tempId = `temp-${Date.now()}`;

    const tempMessage = {
      id: tempId,
      chat_id: activeChatId,
      user_prompt: message,
      created_at: new Date().toISOString(),
      llm_response: "",
      mode,
      isTemp: true,
      isStreaming: true,
    };

    setMessagesCache((prev) => {
      const id = activeChatId || "new";
      const chatData = prev[id] || {
        messages: [],
        next: null,
        isLoading: false,
        isFetchingMore: false,
      };
      return {
        ...prev,
        [id]: {
          ...chatData,
          messages: [...chatData.messages, tempMessage],
        },
      };
    });

    streamingRef.current = true;
    setIsStreaming(true);
    abortControllerRef.current = new AbortController();

    let assistantMessage = "";
    let updateScheduled = false;
    let lastUpdateTime = Date.now();
    const UPDATE_INTERVAL = 30;

    const scheduleUpdate = () => {
      if (updateScheduled) return;

      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTime;

      if (timeSinceLastUpdate < UPDATE_INTERVAL) {
        setTimeout(() => {
          requestAnimationFrame(performUpdate);
        }, UPDATE_INTERVAL - timeSinceLastUpdate);
        updateScheduled = true;
        return;
      }

      requestAnimationFrame(performUpdate);
      updateScheduled = true;
    };

    const performUpdate = () => {
      setMessagesCache((prev) => {
        const id = activeChatId || "new";
        const chatData = prev[id];
        if (!chatData) return prev;

        return {
          ...prev,
          [id]: {
            ...chatData,
            messages: chatData.messages.map((m) =>
              m.id === tempId
                ? { ...m, llm_response: assistantMessage, isStreaming: true }
                : m,
            ),
          },
        };
      });
      lastUpdateTime = Date.now();
      updateScheduled = false;
    };

    try {
      const baseURL = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/`;
      const response = await fetch(`${baseURL}chat/`, {
        method: "POST",
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_prompt: message,
          chat_id: activeChatId,
          mode: mode,
          web_search: webSearch,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);
      if (!response.body) throw new Error("ReadableStream not supported");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        buffer += text;
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || trimmed === "[DONE]") continue;

          const jsonStr = trimmed.startsWith("data: ")
            ? trimmed.slice(6)
            : trimmed;

          try {
            const data = JSON.parse(jsonStr);

            if (data.type === "chunk" && data.content) {
              assistantMessage += data.content;
              scheduleUpdate();
            } else if (data.type === "progress") {
              setLoadingStage(data.stage);
            } else if (data.type === "done") {
              const realChatId = data.chat_id;
              const finalResponse =
                data.final_llm_response?.llm_response ||
                data.llm_response ||
                assistantMessage;

              // eslint-disable-next-line no-loop-func
              setMessagesCache((prev) => {
                const id = activeChatId || "new";
                const chatData = prev[id];
                if (!chatData) return prev;

                const updatedMessages = chatData.messages.map((m) =>
                  m.id === tempId
                    ? {
                        ...m,
                        ...data,
                        llm_response: finalResponse,
                        mode: m.mode || data.mode,
                        isTemp: false,
                        isError: false,
                        isStreaming: false,
                      }
                    : m,
                );

                if (!activeChatId && realChatId) {
                  return {
                    ...prev,
                    [id]: { ...chatData, messages: [] },
                    [realChatId]: {
                      messages: updatedMessages,
                      next: null,
                      isLoading: false,
                      isFetchingMore: false,
                      isStreaming: false,
                    },
                  };
                }

                return {
                  ...prev,
                  [id]: { ...chatData, messages: updatedMessages },
                };
              });

              if (!activeChatId && realChatId) {
                activeChatId = realChatId; // Update local tracker
                setCurrentChatId(realChatId);
                setChatSessions((prev) => {
                  if (prev.find((s) => s.chat_id === realChatId)) return prev;
                  return [
                    {
                      chat_id: realChatId,
                      user_prompt: message,
                      llm_response: finalResponse,
                      created_at: new Date().toISOString(),
                    },
                    ...prev,
                  ].sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at),
                  );
                });
              }
            }
          } catch (e) {
            showErrorToast(e, "Failed to stream and send message");
          }
        }
      }
    } catch (err) {
      if (err.name === "AbortError") {
        return;
      }

      console.error("Streaming failed", err);
      showErrorToast(err, "Failed to send message");

      setMessagesCache((prev) => {
        const id = activeChatId || "new";
        const chatData = prev[id];
        if (!chatData) return prev;

        return {
          ...prev,
          [id]: {
            ...chatData,
            messages: chatData.messages.map((m) =>
              m.id === tempId
                ? {
                    ...m,
                    isError: true,
                    isTemp: false,
                    isStreaming: false,
                    llm_response:
                      assistantMessage || "Failed to generate response.",
                  }
                : m,
            ),
          },
        };
      });
    } finally {
      streamingRef.current = false;
      setIsStreaming(false);
      setLoadingStage(null);
      abortControllerRef.current = null;
    }
  };

  const toggleAssistant = useCallback(() => {
    setIsOpen((prev) => {
      const newState = !prev;
      if (newState && chatSessions.length === 0) {
        fetchSessions();
      }
      return newState;
    });
  }, [chatSessions.length, fetchSessions]);

  const selectChatSession = useCallback(
    (chatId) => {
      setCurrentChatId(chatId);
      fetchMessages(chatId);
    },
    [fetchMessages],
  );

  const createNewChat = useCallback(() => {
    setCurrentChatId(null);
    setMessagesCache((prev) => {
      const newState = { ...prev };
      delete newState["new"];
      return newState;
    });
  }, []);

  const deleteChatSession = async (chatId) => {
    try {
      await apiInstance.delete(`user-chats/delete_chat_space/${chatId}/`, {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
      });

      setChatSessions((prev) => prev.filter((s) => s.chat_id !== chatId));

      setMessagesCache((prev) => {
        const { [chatId]: _, ...rest } = prev;
        return rest;
      });

      if (currentChatId === chatId) {
        setCurrentChatId(null);
      }

      showSuccessToast("Chat deleted successfully");
    } catch (err) {
      console.error("Failed to delete session", err);
      showErrorToast(err, "Failed to delete chat");
    }
  };

  const enrichCompany = async ({
    companyId,
    chatId,
    apolloFetched,
    perplexityFetched,
  }) => {
    try {
      const response = await apiInstance.post(
        "chat/manual-enrichment/",
        {
          company_id: companyId,
          chat_id: chatId,
          apollo_enrichment_fetched: apolloFetched,
          perplexity_enrichment_fetched: perplexityFetched,
        },
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.success) {
        const enrichedData = response.data.data || {};

        setMessagesCache((prev) => {
          const chatState = prev[chatId];
          if (!chatState) return prev;

          const updatedMessages = chatState.messages.map((msg) => {
            let newMsg = { ...msg };

            // Update searched_results
            if (newMsg.searched_results) {
              newMsg.searched_results = newMsg.searched_results.map((res) => {
                if (
                  String(res.id) === String(companyId) ||
                  String(res.company_id) === String(companyId)
                ) {
                  return { ...res, ...enrichedData };
                }
                return res;
              });
            }

            // Update web_results
            if (newMsg.web_results) {
              newMsg.web_results = newMsg.web_results.map((res) => {
                if (
                  String(res.id) === String(companyId) ||
                  String(res.company_id) === String(companyId)
                ) {
                  return { ...res, ...enrichedData };
                }
                return res;
              });
            }

            return newMsg;
          });

          return {
            ...prev,
            [chatId]: {
              ...chatState,
              messages: updatedMessages,
            },
          };
        });

        showSuccessToast("Manual enrichment is done");
        return true;
      }
      return false;
    } catch (err) {
      console.error("Failed to trigger enrichment", err);
      showErrorToast(err, "Failed to trigger enrichment");
      return false;
    }
  };

  const respondToCompany = async ({
    chatId,
    companyName,
    action,
    isWeb,
    companyData,
  }) => {
    try {
      const response = await apiInstance.post(
        "chat/company-response/",
        {
          chat_id: chatId,
          company_name: companyName,
          action: action,
          is_web: isWeb,
          company_data: companyData,
        },
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.success) {
        setMessagesCache((prev) => {
          const chatState = prev[chatId];
          if (!chatState) return prev;

          const updatedMessages = chatState.messages.map((msg) => {
            const resultsKey = isWeb ? "web_results" : "searched_results";
            if (!msg[resultsKey]) return msg;

            const updatedResults = msg[resultsKey].map((res) => {
              const resName =
                res.company_name ||
                res.company_details?.company_name ||
                res.name;
              if (resName === companyName) {
                return {
                  ...res,
                  is_accepted: action === "accept",
                  is_declined: action === "decline",
                };
              }
              return res;
            });

            return { ...msg, [resultsKey]: updatedResults };
          });

          return {
            ...prev,
            [chatId]: { ...chatState, messages: updatedMessages },
          };
        });
        showSuccessToast(
          response.data.message || `Company ${action}ed successfully`,
        );
        return true;
      }
      return false;
    } catch (err) {
      console.error("Failed to process company response", err);
      showErrorToast(err, "Failed to process selection");
      return false;
    }
  };

  const deleteMessage = async (chatId, messageId) => {
    try {
      await apiInstance.delete(`user-chats/delete_message/${messageId}/`, {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
      });

      setMessagesCache((prev) => {
        const chatData = prev[chatId];
        if (!chatData) return prev;
        const newMessages = chatData.messages.filter((m) => m.id !== messageId);
        return {
          ...prev,
          [chatId]: {
            ...chatData,
            messages: newMessages,
          },
        };
      });

      showSuccessToast("Message deleted");
    } catch (err) {
      console.error("Failed to delete message", err);
      showErrorToast(err, "Failed to delete message");
    }
  };

  const activeMessages = useMemo(() => {
    const id = currentChatId || "new";
    return messagesCache[id]?.messages || [];
  }, [messagesCache, currentChatId]);

  const activeChatData = useMemo(() => {
    return (
      messagesCache[currentChatId || "new"] || {
        messages: [],
        next: null,
        isLoading: false,
        isFetchingMore: false,
      }
    );
  }, [messagesCache, currentChatId]);

  const value = {
    isOpen,
    isLoadingSessions,
    isLoadingMessages: activeChatData.isLoading,
    isFetchingMoreSessions,
    isFetchingMoreMessages: activeChatData.isFetchingMore,
    isStreaming,
    loadingStage,
    chatSessions,
    activeMessages,
    currentChatId,
    toggleAssistant,
    sendMessage,
    createNewChat,
    selectChatSession,
    refreshSessions: () => fetchSessions(),
    loadMoreSessions: () => sessionsNextPage && fetchSessions(sessionsNextPage),
    loadMoreMessages: () =>
      activeChatData.next && fetchMessages(currentChatId, activeChatData.next),
    hasMoreSessions: !!sessionsNextPage,
    hasMoreMessages: !!activeChatData.next,
    deleteChatSession,
    deleteMessage,
    enrichCompany,
    respondToCompany,
  };

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
};

export const useAssistant = () => {
  const context = useContext(AssistantContext);
  if (!context)
    throw new Error("useAssistant must be used within AssistantProvider");
  return context;
};
